import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import Switch from "@client/components/ui/Switch";
import TextArea from "@client/components/ui/TextArea";
import notificationStore from "@client/stores/NotificationStore";
import DomUtils from "@client/utils/DomUtils";
import Style from "@client/utils/Style";
import { Customer } from "@typings/Customer";
import LicenceHelper, { Licence } from "@typings/Licence";

import copySvg from "./images/copy.svg";
import downloadSvg from "./images/download.svg";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0 32px 0;
`;
const LicenceImage = styled.div<{ image: string }>`
  ${Style.exactSize("96px")}
  background: url('${(props: any) => props.image}') center no-repeat;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 32px 32px 32px;
`;
const EmailInputs = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 32px;
  flex-grow: 1;
`;
const LicenceTextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
const InputLabel = styled.div`
  font-size: 10px;
  font-weight: ${Style.fontWeights.medium};
  color: ${Style.palette.grayscale[3]};
  text-transform: uppercase;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;
const EmailSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const SendEmailLabel = styled.div`
  color: ${Style.palette.grayscale[4]};
  margin-left: 16px;
`;
const CustomerLabel = styled.span`
  color: #1d1e2e;
  margin-left: 8px;
`;
const RetrieveOptions = styled.div`
  display: flex;
`;
const CopyButton = styled.div`
  width: 16px;
  height: 16px;
  cursor: pointer;
  background: url("${copySvg}") center no-repeat;
`;
const DownloadButton = styled.div`
  width: 16px;
  height: 16px;
  cursor: pointer;
  background: url("${downloadSvg}") center no-repeat;
  margin-left: 12px;
`;

type Props = {
  sending: boolean;
  onRequestClose: () => void;
  onSendClick: () => void;
  licenceImage: any;
  licenceText: string;
  customer: Customer | null;
  licence: Licence | null;
};

type State = {
  sendEmail: boolean;
};

@observer
class LicenceModalEmail extends React.Component<Props, State> {
  state: State = {
    sendEmail: true,
  };

  handleCopyClick() {
    const succesful = DomUtils.copyTextToClipboard(this.props.licenceText);

    if (succesful) {
      notificationStore.alert("The licence has been copied to clipboard");
    }
  }

  handleDownloadClick() {
    DomUtils.downloadAsTextFile(
      this.props.licenceText,
      `${LicenceHelper.getName(this.props.licence, this.props.customer)}.pem`,
    );
  }

  renderEmailButtons() {
    const email = this.props.customer ? this.props.customer.email : null;

    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={this.props.sending}
        >
          Cancel
        </Button>
        {this.state.sendEmail && email ? (
          this.props.sending ? (
            <ButtonLoading large>Sending...</ButtonLoading>
          ) : (
            <Button
              large
              onClick={() => {
                this.props.onSendClick();
              }}
              disabled={this.props.sending}
            >
              Send
            </Button>
          )
        ) : (
          <Button large onClick={this.props.onRequestClose}>
            Done
          </Button>
        )}
      </Buttons>
    );
  }

  renderEmailInputs() {
    const name = this.props.customer ? this.props.customer.name : "";
    const email = this.props.customer ? this.props.customer.email : null;
    const customerLabel = <CustomerLabel>{name}</CustomerLabel>;

    return (
      <EmailInputs>
        <LicenceTextAreaWrapper>
          <InputLabel>Licence</InputLabel>
          <TextArea
            readOnly
            width="100%"
            height="226px"
            style={{ marginTop: "4px", overflow: "hidden" }}
            value={this.props.licenceText}
          />
        </LicenceTextAreaWrapper>
        <Row>
          <EmailSwitchWrapper>
            <Switch
              noLabel
              checked={email ? this.state.sendEmail : false}
              onChange={checked => {
                this.setState({ sendEmail: Boolean(checked) });
              }}
              hideFocusIndicator
              disabled={this.props.sending || !email}
            />
            {email ? (
              <SendEmailLabel>
                Email a copy to
                {customerLabel}
              </SendEmailLabel>
            ) : (
              <SendEmailLabel>
                There&apos;s no email address for
                {customerLabel}
              </SendEmailLabel>
            )}
          </EmailSwitchWrapper>
          <RetrieveOptions>
            <CopyButton
              onClick={() => {
                this.handleCopyClick();
              }}
            />
            <DownloadButton
              onClick={() => {
                this.handleDownloadClick();
              }}
            />
          </RetrieveOptions>
        </Row>
      </EmailInputs>
    );
  }

  renderContent() {
    return (
      <Content>
        <Header>
          <LicenceImage image={this.props.licenceImage} />
        </Header>
        {this.renderEmailInputs()}
        {this.renderEmailButtons()}
      </Content>
    );
  }

  render() {
    return <Wrapper>{this.renderContent()}</Wrapper>;
  }
}

export default LicenceModalEmail;
